import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card } from '@/components/ui/card';
import {
  Home,
  MessageSquare,
  Folder,
  Lightbulb,
  BookOpen,
  Shield,
  User as UserIcon,
} from 'lucide-react';
import { ScribeEmbed } from '@/components/anleitung/ScribeEmbed';

const Anleitung = () => {
  const [activeSection, setActiveSection] = useState('1-erste-schritte--anmeldung');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const sections = useMemo(() => [
    { id: '1-erste-schritte--anmeldung', title: 'Erste Schritte & Anmeldung', icon: Home },
    { id: '2-chatbot-assistent', title: 'Chatbot-Assistent', icon: MessageSquare },
    { id: '3-datei-verwaltung', title: 'Datei-Verwaltung', icon: Folder },
    { id: '4-beispiel-datenbank', title: 'Beispiel-Datenbank', icon: Lightbulb },
  ], []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(section.id);
        if (element) {
          const elementTop = element.offsetTop;
          if (scrollTop >= elementTop - 200) {
            setActiveSection(section.id);
            return;
          }
        }
      }
      setActiveSection(sections[0].id);
    };

    handleScroll();
    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const RoleBadge = ({ type, children }: { type: string; children: React.ReactNode }) => {
    if (type === 'admin') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-secondary text-secondary-foreground">
          <Shield className="h-3 w-3" />
          {children}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
        <UserIcon className="h-3 w-3" />
        {children}
      </span>
    );
  };

  const WarningBox = ({ children }: { children: React.ReactNode }) => (
    <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded text-sm text-muted-foreground">
      {children}
    </div>
  );

  const StatusIndicator = ({ type, children }: { type: string; children: React.ReactNode }) => {
    const colors = {
      pending: 'text-yellow-600',
      processed: 'text-green-600',
      failed: 'text-red-600',
    };
    const icons = {
      pending: '⏳',
      processed: '✅',
      failed: '❌',
    };
    return (
      <div className="flex items-center gap-2 text-sm my-2">
        <span className={`w-24 font-semibold ${colors[type as keyof typeof colors]}`}>
          {icons[type as keyof typeof icons]} {children}
        </span>
      </div>
    );
  };

  return (
    <div ref={scrollContainerRef} className="h-full bg-background overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 py-8 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="h-10 w-10 text-primary" />
              <div>
                <h1 className="text-3xl font-bold text-foreground">Schulungsunterlagen</h1>
                <p className="text-lg text-muted-foreground mt-2">
                  Lernen Sie, wie Sie den L&R Chatbot-Assistent effektiv nutzen können.
                </p>
              </div>
            </div>

            <hr className="my-8 border-border" />

            {/* Section 1: Erste Schritte */}
            <section id="1-erste-schritte--anmeldung" className="scroll-mt-8 mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-6">1. Erste Schritte & Anmeldung</h2>
              <p className="text-muted-foreground mb-4">
                Willkommen beim L&R Chatbot-Assistent! Die Anwendung hilft Ihnen bei Maschinenfehlerdiagnosen und Neustartprozeduren für die Produktionsanlage mit der Anlagennummer 1221401.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Anmeldung</h3>
              <p className="text-muted-foreground mb-4">
                <strong className="font-semibold text-foreground">Azure AD/Entra ID SSO:</strong> Melden Sie sich mit Ihrem Microsoft-Konto an (empfohlen für Produktionsumgebungen)
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Benutzerrollen verstehen</h3>
              <p className="text-muted-foreground mb-2">
                <RoleBadge type="user">User</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                <strong className="font-semibold text-foreground">Standard-Benutzer:</strong> Zugriff auf Chat-Interface, Ansicht verarbeiteter Dokumente, Verwaltung von Beispielen in der Datenbank
              </p>
              <p className="text-muted-foreground mb-2">
                <RoleBadge type="admin">Admin</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                <strong className="font-semibold text-foreground">Administrator:</strong> Vollzugriff inklusive Datei-Upload, Dokumentenverarbeitung, VDB-Rebuild und alle Management-Funktionen
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Navigation verstehen</h3>
              <p className="text-muted-foreground mb-4">
                Verwenden Sie die Sidebar (☰-Symbol links oben), um zwischen den verschiedenen Bereichen der Anwendung zu navigieren:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li><strong className="font-semibold text-foreground">Chatbot-Assistent:</strong> Hauptbereich für Konversationen mit der KI</li>
                <li><strong className="font-semibold text-foreground">Datei-Verwaltung:</strong> Upload und Verarbeitung von Dokumenten</li>
                <li><strong className="font-semibold text-foreground">Beispiel-Datenbank:</strong> Trainingsbeispiele für bessere Antworten</li>
                <li><strong className="font-semibold text-foreground">Anleitung:</strong> Diese Hilfeseite</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Benutzerprofil & Abmeldung</h3>
              <p className="text-muted-foreground mb-4">
                Ihr Benutzerprofil wird im Header rechts oben angezeigt. Hier sehen Sie Ihren Namen, E-Mail und Ihre Rolle. Klicken Sie auf das Abmelde-Symbol, um sich auszuloggen.
              </p>

              <ScribeEmbed
                src="https://scribehow.com/embed/lruc2login__kS1jbgB-Tu-7LUOxYnlajg?removeLogo=true&as=scrollable"
                title="Anmeldeprozess Schritt für Schritt"
                description=""
              />
            </section>

            <hr className="my-8 border-border" />

            {/* Section 2: Chatbot-Assistent */}
            <section id="2-chatbot-assistent" className="scroll-mt-8 mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-6">2. Chatbot-Assistent</h2>
              <p className="text-muted-foreground mb-4">
                <RoleBadge type="user">User</RoleBadge> <RoleBadge type="admin">Admin</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                Der Chatbot-Assistent ist das Herzstück der Anwendung. Hier können Sie Fragen stellen und Antworten zu Maschinenfehler-Codes und Neustartprozeduren erhalten.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Chat-Grundlagen</h3>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li><strong className="font-semibold text-foreground">Nachricht senden:</strong> Geben Sie Ihre Frage im Eingabefeld unten ein und drücken Sie Enter oder klicken Sie auf Senden</li>
                <li><strong className="font-semibold text-foreground">Bilder hochladen:</strong> Klicken Sie auf das Bild-Symbol, um Screenshots von Fehlermeldungen oder Maschinenzuständen anzuhängen</li>
                <li><strong className="font-semibold text-foreground">Streaming-Antworten:</strong> Die KI antwortet in Echtzeit mit Typing-Animation während der Verarbeitung</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Sitzungsverwaltung</h3>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li><strong className="font-semibold text-foreground">Neue Sitzung erstellen:</strong> Klicken Sie auf den "+"-Button in der Sitzungsliste, um ein neues Gespräch zu beginnen</li>
                <li><strong className="font-semibold text-foreground">Zwischen Sitzungen wechseln:</strong> Wählen Sie eine vorhandene Sitzung aus der Liste auf der linken Seite</li>
                <li><strong className="font-semibold text-foreground">Sitzungstitel bearbeiten:</strong> Klicken Sie auf den Titel einer Sitzung, um ihn anzupassen</li>
                <li><strong className="font-semibold text-foreground">Sitzungen löschen:</strong> Verwenden Sie das Menü in der Sitzungsliste, um alte Gespräche zu entfernen</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Tool-Aufrufe verstehen</h3>
              <p className="text-muted-foreground mb-4">
                Die KI verwendet verschiedene Tools, um Ihre Fragen zu beantworten. Sie sehen diese Tool-Aufrufe in Echtzeit während der Verarbeitung:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li><strong className="font-semibold text-foreground">keyword_search:</strong> Sucht nach exakten Textübereinstimmungen in Dokumenten</li>
                <li><strong className="font-semibold text-foreground">semantic_search:</strong> Findet semantisch ähnliche Informationen</li>
                <li><strong className="font-semibold text-foreground">combined_search:</strong> Kombiniert beide Suchmethoden</li>
                <li><strong className="font-semibold text-foreground">chunk_context:</strong> Erweitert Suchergebnisse um zusätzlichen Kontext</li>
                <li><strong className="font-semibold text-foreground">few_shot_retrieval:</strong> Ruft Trainingsbeispiele ab</li>
                <li><strong className="font-semibold text-foreground">web_search:</strong> Sucht im Internet nach aktuellen Informationen</li>
              </ul>

              <ScribeEmbed
                src="https://scribehow.com/embed/lr-uc2-chat__GWVyb-xSSvunNaRxJPBJtQ?removeLogo=true&as=scrollable"
                title="Chat-Interface verwenden"
                description=""
              />
            </section>

            <hr className="my-8 border-border" />

            {/* Section 3: Datei-Verwaltung */}
            <section id="3-datei-verwaltung" className="scroll-mt-8 mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-6">3. Datei-Verwaltung</h2>
              <p className="text-muted-foreground mb-4">
                Die Datei-Verwaltung ermöglicht das Hochladen, Verarbeiten und Verwalten von technischen Dokumenten, die der Chatbot für Antworten verwendet.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Admin-Funktionen</h3>
              <p className="text-muted-foreground mb-2">
                <RoleBadge type="admin">Nur Admin</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                Administratoren haben vollständige Kontrolle über die Dokumentenverwaltung:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li><strong className="font-semibold text-foreground">Dateien hochladen:</strong> Einzelne Dateien oder ZIP-Archive hochladen (unterstützte Formate: PDF, DOCX, PPTX, TXT)</li>
                <li><strong className="font-semibold text-foreground">Dokumente löschen:</strong> Nicht mehr benötigte Dokumente aus dem System entfernen</li>
                <li><strong className="font-semibold text-foreground">Neue Dokumente verarbeiten:</strong> Hochgeladene Dateien werden zu Markdown konvertiert, in Chunks aufgeteilt und in der Vektordatenbank gespeichert</li>
                <li><strong className="font-semibold text-foreground">Fehlerhafte wiederholen:</strong> Verarbeitung von zuvor fehlgeschlagenen Dateien erneut versuchen</li>
                <li><strong className="font-semibold text-foreground">VDB neu aufbauen:</strong> Vektordatenbank komplett neu erstellen (bei Inkonsistenzen oder nach Migrationen)</li>
              </ul>

              <WarningBox>
                <strong className="font-semibold text-foreground">Warnung:</strong> VDB-Rebuild blockiert die Anwendung für andere Nutzer und kann mehrere Minuten dauern.
              </WarningBox>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Verarbeitungsstatus verstehen</h3>
              <StatusIndicator type="pending">Pending</StatusIndicator>
              <p className="text-muted-foreground mb-2">Datei wurde hochgeladen, wartet auf Verarbeitung</p>
              <StatusIndicator type="processed">Processed</StatusIndicator>
              <p className="text-muted-foreground mb-2">Datei wurde erfolgreich verarbeitet und ist durchsuchbar</p>
              <StatusIndicator type="failed">Failed</StatusIndicator>
              <p className="text-muted-foreground mb-4">Verarbeitung ist fehlgeschlagen, verwenden Sie "Fehlerhafte wiederholen"</p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Wissensdatenbank durchsuchen (User)</h3>
              <p className="text-muted-foreground mb-2">
                <RoleBadge type="user">User</RoleBadge> <RoleBadge type="admin">Admin</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                Alle Benutzer können verarbeitete Dokumente in Markdown-Ansicht betrachten, die Wissensdatenbank durchsuchen und die Zuordnung zwischen Eingabe- und Ausgabedateien nachvollziehen.
              </p>

              <ScribeEmbed
                src="https://scribehow.com/embed/lr-uc2-document-storage__vDwBV4o_S4ywcVskhT0T2Q?removeLogo=true&as=scrollable"
                title="Dateien hochladen und verarbeiten"
                description=""
              />
            </section>

            <hr className="my-8 border-border" />

            {/* Section 4: Beispiel-Datenbank */}
            <section id="4-beispiel-datenbank" className="scroll-mt-8 mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-6">4. Beispiel-Datenbank</h2>
              <p className="text-muted-foreground mb-4">
                Die Beispiel-Datenbank (Few-Shot Learning) ermöglicht es, die KI durch Frage-Antwort-Beispiele zu trainieren und die Qualität der Antworten zu verbessern.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Neues Beispiel hinzufügen (Admin)</h3>
              <p className="text-muted-foreground mb-2">
                <RoleBadge type="admin">Nur Admin</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                Nur Administratoren können neue Trainingsbeispiele erstellen:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li><strong className="font-semibold text-foreground">Frage eingeben:</strong> Die Frage, die ein Benutzer stellen könnte</li>
                <li><strong className="font-semibold text-foreground">Antwort eingeben:</strong> Die ideale Antwort auf diese Frage</li>
                <li><strong className="font-semibold text-foreground">Metadaten hinzufügen:</strong></li>
                <ul className="list-disc list-inside ml-6 space-y-1">
                  <li>Machine Part (z.B. PMK, Drucker, MKT)</li>
                  <li>Submachine Part (optional, z.B. Zufuehrung)</li>
                  <li>Error Code (optional, z.B. F04, Fault 0020)</li>
                  <li>Created By (automatisch aus Benutzerprofil)</li>
                </ul>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Gute vs. schlechte Beispiele</h3>
              <p className="text-muted-foreground mb-2">✅ <strong className="font-semibold text-foreground">Gutes Beispiel (Standard)</strong></p>
              <p className="text-muted-foreground mb-4">
                Ein Beispiel, das die KI nachahmen sollte. Markieren Sie Beispiele als "gut", wenn sie korrekte, hilfreiche und gut formulierte Antworten enthalten.
              </p>
              <p className="text-muted-foreground mb-2">❌ <strong className="font-semibold text-foreground">Schlechtes Beispiel (Zu vermeiden)</strong></p>
              <p className="text-muted-foreground mb-4">
                Ein Beispiel, das zeigt, was die KI NICHT tun sollte. Geben Sie im Feld "Warum ist das schlecht?" an, was an der Antwort problematisch ist (z.B. zu vage, falsche Informationen, schlechte Struktur).
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Beispiele durchsuchen (User)</h3>
              <p className="text-muted-foreground mb-2">
                <RoleBadge type="user">User</RoleBadge> <RoleBadge type="admin">Admin</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                Alle Benutzer können Trainingsbeispiele durchsuchen und anzeigen:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li><strong className="font-semibold text-foreground">Liste anzeigen:</strong> Sehen Sie alle Beispiele in der Übersicht</li>
                <li><strong className="font-semibold text-foreground">Nach Kategorie filtern:</strong> Filtern Sie nach Machine Part, Error Code oder anderen Kriterien</li>
                <li><strong className="font-semibold text-foreground">Details anzeigen:</strong> Betrachten Sie einzelne Beispiele mit allen Metadaten</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Beispiele verwalten (Admin)</h3>
              <p className="text-muted-foreground mb-2">
                <RoleBadge type="admin">Nur Admin</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                Administratoren können Trainingsbeispiele vollständig verwalten:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li><strong className="font-semibold text-foreground">Beispiele bearbeiten:</strong> Aktualisieren Sie vorhandene Beispiele (werden automatisch neu eingebettet)</li>
                <li><strong className="font-semibold text-foreground">Beispiele löschen:</strong> Entfernen Sie nicht mehr relevante Beispiele</li>
                <li><strong className="font-semibold text-foreground">Import/Export:</strong> Laden Sie Beispiele als JSON-Datei hoch oder herunter (nützlich für Backups)</li>
                <li><strong className="font-semibold text-foreground">Bulk-Upload:</strong> Fügen Sie mehrere Beispiele gleichzeitig hinzu</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Few-Shot-VDB neu aufbauen</h3>
              <p className="text-muted-foreground mb-2">
                <RoleBadge type="admin">Nur Admin</RoleBadge>
              </p>
              <p className="text-muted-foreground mb-4">
                Die Few-Shot-Vektordatenbank speichert Embeddings der Trainingsbeispiele. Ein Neuaufbau ist sinnvoll nach:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li>Vielen Änderungen an bestehenden Beispielen</li>
                <li>Bulk-Import großer Beispielmengen</li>
                <li>Inkonsistenzen in der Beispiel-Datenbank</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Best Practices</h3>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4 ml-4">
                <li>Fügen Sie spezifische Beispiele für häufige Fehler-Codes hinzu</li>
                <li>Verwenden Sie konsistente Formatierung in Ihren Antworten</li>
                <li>Markieren Sie problematische Beispiele als "schlecht" mit klarer Begründung</li>
                <li>Aktualisieren Sie Beispiele regelmäßig basierend auf Feedback</li>
                <li>Nutzen Sie Metadaten für bessere Kategorisierung</li>
              </ul>

              <ScribeEmbed
                src="https://scribehow.com/embed/lr-uc2-few-shot__Uqak2KMoQgCWAnd0BPOIlw?removeLogo=true&as=scrollable"
                title="Beispiel hinzufügen und gute/schlechte Markierung"
                description=""
              />
            </section>
          </div>

          {/* Table of Contents - Sticky Sidebar */}
          <div className="hidden lg:block">
            <div className="sticky top-8">
              <Card className="p-4">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <span className="text-sm">≡</span> Auf dieser Seite
                </h3>
                <nav className="space-y-2">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left text-sm py-2 px-3 rounded-md transition-colors flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Anleitung;
